type Props = {
  composite: number | null | undefined;
  periodLabel: string;
  max?: number;
  tier?: string | null;
  size?: number;
};

const TIER_STROKE: Record<string, string> = {
  Champion: "#16a34a",
  Active: "#2563eb",
  Developing: "#d97706",
  "At-Risk": "#dc2626",
};

const CompositeDonut = ({
  composite,
  periodLabel,
  max = 5,
  tier,
  size = 220,
}: Props) => {
  const v = Math.min(max, Math.max(0, composite ?? 0));
  const displayScore = v % 1 === 0 ? v.toFixed(0) : v.toFixed(2);

  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.4;
  const strokeWidth = size * 0.064;
  const circumference = 2 * Math.PI * r;
  const progress = composite != null ? v / max : 0;
  const offset = circumference * (1 - progress);
  const color = tier ? (TIER_STROKE[tier] ?? "#9ca3af") : "#e5e7eb";

  const scoreFontSize = Math.round(size * 0.182);
  const unitFontSize = Math.round(size * 0.082);
  const labelFontSize = Math.max(9, Math.round(size * 0.05));

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label={`Score: ${displayScore}`}
        role="img"
      >
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        {composite != null && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        )}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center select-none">
        <div className="flex items-baseline gap-1 leading-none">
          <span
            className="font-black text-gray-900 tracking-tight"
            style={{ fontSize: scoreFontSize }}
          >
            {displayScore}
          </span>
          <span
            className="font-semibold text-gray-400"
            style={{ fontSize: unitFontSize }}
          >
            / {max}
          </span>
        </div>
        <span
          className="mt-1.5 font-semibold uppercase tracking-widest text-gray-500"
          style={{ fontSize: labelFontSize }}
        >
          {periodLabel || "Score"}
        </span>
      </div>
    </div>
  );
};

export default CompositeDonut;
